import React, { useState, useMemo, useEffect, useRef } from "react";
import { Box, Grid, Stack, Typography, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useDashboardSummaryQuery } from "../../../../hooks/api_hooks/dashboardSummary";
import AlertBanners from "../dashboard_page/AlertBanners";

export const DATA_CATEGORY_TAGS = {
  PHONE: "phone_number",
  EMAIL: "personal_email",
  ADDRESS: "home_address",
  EMPLOYMENT: "employment_history",
  SSN: "social_security",
  FAMILY: "family_relationships",
};

const CATEGORY_LABELS = {
  phone_number: "Mobile Phone",
  personal_email: "Personal Email",
  home_address: "Home Address",
  employment_history: "Employment History",
  social_security: "Social Security",
  family_relationships: "Family Relationships",
};

const T = {
  bg: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E2E8F0",
  ink: "#0F172A",
  muted: "#64748B",
  faint: "#94A3B8",
  accent: "#09b2f0",
  accentDark: "#07a0d9",
  accentBg: "#F0F9FF",
};

function relativeDate(dateStr) {
  if (!dateStr) return "—";
  const now = new Date();
  const then = new Date(dateStr);
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const diffDays = Math.round((nowDay - thenDay) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.round(diffDays / 30.44);
  if (diffMonths === 1) return "1 month ago";
  if (diffMonths < 12) return `${diffMonths} months ago`;
  const diffYears = Math.round(diffDays / 365.25);
  return diffYears === 1 ? "1 year ago" : `${diffYears} years ago`;
}

function SendIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M22 2L11 13"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 2L15 22L11 13L2 9L22 2Z"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function RadarIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="black" strokeWidth="2" />
      <path
        d="M12 7v5l3 3"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShieldCheckIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="black"
        strokeWidth="2.5"
        strokeDasharray="48 8"
        strokeLinecap="round"
      />
      <path
        d="M8.5 12.5l2.5 2.5 4.5-5"
        stroke="black"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PhoneIcon({ size = 18, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect
        x="6"
        y="3"
        width="12"
        height="18"
        rx="2"
        stroke={color}
        strokeWidth="2"
      />
      <path
        d="M12 18h.01"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EmailIcon({ size = 18, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2"
        stroke={color}
        strokeWidth="2"
      />
      <path
        d="M3 7l9 6 9-6"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HomeIcon({ size = 18, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M3 10l9-7 9 7v10a2 2 0 01-2 2H5a2 2 0 01-2-2V10z"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 21V12h6v9"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BriefcaseIcon({ size = 18, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="7"
        width="18"
        height="13"
        rx="2"
        stroke={color}
        strokeWidth="2"
      />
      <path
        d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"
        stroke={color}
        strokeWidth="2"
      />
      <path d="M3 13h18" stroke={color} strokeWidth="2" />
    </svg>
  );
}

function ShieldIcon({ size = 18, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 12l2 2 4-4"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UsersIcon({ size = 18, color = "#6B7280" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="7" r="4" stroke={color} strokeWidth="2" />
      <path
        d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="17" cy="7" r="3" stroke={color} strokeWidth="2" />
      <path
        d="M23 21v-2a3 3 0 00-3-3"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CategoryIcon({ tag, size = 18 }) {
  const color = "#9CA3AF";
  if (tag === DATA_CATEGORY_TAGS.PHONE)
    return <PhoneIcon size={size} color={color} />;
  if (tag === DATA_CATEGORY_TAGS.EMAIL)
    return <EmailIcon size={size} color={color} />;
  if (tag === DATA_CATEGORY_TAGS.ADDRESS)
    return <HomeIcon size={size} color={color} />;
  if (tag === DATA_CATEGORY_TAGS.EMPLOYMENT)
    return <BriefcaseIcon size={size} color={color} />;
  if (tag === DATA_CATEGORY_TAGS.SSN)
    return <ShieldIcon size={size} color={color} />;
  if (tag === DATA_CATEGORY_TAGS.FAMILY)
    return <UsersIcon size={size} color={color} />;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
      }}
    />
  );
}

function Card({ children, sx = {} }) {
  return (
    <Box
      sx={{
        bgcolor: T.surface,
        border: "1px solid #E2E8F0",
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionTitle({ title, subtitle, action }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #F1F5F9",
        backgroundColor: "#FAFBFD",
      }}
    >
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: "1rem",
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{ fontSize: "0.78rem", color: T.faint, mt: 0.25 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
}

function ActionButton({ label, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 1.5,
        py: 0.75,
        bgcolor: "#f59e0b",
        borderRadius: 2,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        transition: "opacity 0.15s",
        flexShrink: 0,
        "&:hover": { opacity: 0.85 },
      }}
    >
      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 700,
          color: "#000",
          whiteSpace: "nowrap",
        }}
      >
        {label} →
      </Typography>
    </Box>
  );
}

function MetricCard({ title, value, sub, icon }) {
  return (
    <Box
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 2,
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        p: 2,
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1.5}
      >
        <Box
          sx={{
            bgcolor: "#F9FAFB",
            borderRadius: 1,
            p: 0.75,
            display: "flex",
            alignItems: "center",
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: T.faint,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            textAlign: "right",
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: "2.2rem",
          fontWeight: 800,
          color: T.ink,
          lineHeight: 1,
          letterSpacing: "-1.5px",
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontSize: "0.75rem", color: T.faint, mt: 0.75, display: "block" }}
      >
        {sub}
      </Typography>
    </Box>
  );
}

function DataExposureTable({ categories }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 600 : false,
  );

  const enriched = useMemo(
    () =>
      categories.map((c) => ({ ...c, label: CATEGORY_LABELS[c.tag] ?? c.tag })),
    [categories],
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  if (isMobile) {
    return (
      <Box sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 80px",
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          {["Data Type", "Protected"].map((h) => (
            <Typography
              key={h}
              variant="caption"
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                color: T.faint,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                textAlign: h === "Data Type" ? "left" : "center",
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>
        {enriched.map((cat, i) => (
          <Box
            key={cat.tag}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 80px",
              px: 1.5,
              py: 1.25,
              borderBottom:
                i < enriched.length - 1 ? `1px solid ${T.border}` : "none",
              alignItems: "center",
              bgcolor: T.surface,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CategoryIcon tag={cat.tag} size={13} />
              <Typography
                variant="body2"
                sx={{ fontSize: "0.875rem", fontWeight: 500, color: T.ink }}
              >
                {cat.label}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                textAlign: "center",
                fontWeight: 500,
                color: (cat.protected ?? 0) > 0 ? T.muted : T.faint,
                display: "block",
              }}
            >
              {(cat.protected ?? 0) > 0 ? cat.protected : "—"}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 110px",
          px: 2.5,
          py: 1,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        {["Data Type", "Protected"].map((h) => (
          <Typography
            key={h}
            variant="caption"
            sx={{
              fontSize: "0.68rem",
              fontWeight: 700,
              color: T.faint,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              textAlign: h === "Data Type" ? "left" : "center",
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>
      <Box>
        {enriched.map((cat, i) => (
          <Box
            key={cat.tag}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 110px",
              px: 2.5,
              py: 1.25,
              borderBottom:
                i < enriched.length - 1 ? `1px solid ${T.border}` : "none",
              alignItems: "center",
              transition: "background 0.15s",
              cursor: "pointer",
              "&:hover": { bgcolor: T.accentBg },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <CategoryIcon tag={cat.tag} size={14} />
              <Typography
                variant="body2"
                sx={{ fontSize: "0.875rem", fontWeight: 500, color: T.ink }}
              >
                {cat.label}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                fontWeight: 500,
                color: (cat.protected ?? 0) > 0 ? T.muted : T.faint,
              }}
            >
              {(cat.protected ?? 0) > 0 ? cat.protected : "—"}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}

const STATUS_CONFIG = {
  data_deleted: { dot: "#22C55E", label: "Data Deleted" },
  request_in_progress: { dot: "#F97316", label: "In Progress" },
  request_sent: { dot: "#3B82F6", label: "Request Sent" },
  "Data Deleted": { dot: "#22C55E", label: "Data Deleted" },
  "In Progress": { dot: "#F97316", label: "In Progress" },
  "Request Sent": { dot: "#3B82F6", label: "Request Sent" },
};

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] ?? STATUS_CONFIG.request_sent;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 1,
        py: 0.4,
        borderRadius: 10,
        display: "inline-flex",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: c.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: T.muted,
          whiteSpace: "nowrap",
        }}
      >
        {c.label}
      </Typography>
    </Stack>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useDashboardSummaryQuery();

  // FIX: Only invalidate once on mount using a ref — the previous approach of
  // putting queryClient in the dependency array caused it to re-run on every
  // render on mobile, creating an infinite refresh loop.
  const didInvalidate = useRef(false);
  useEffect(() => {
    if (!didInvalidate.current) {
      didInvalidate.current = true;
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
    }
  }, [queryClient]);

  const summary = data?.summary;
  const categories = data?.categories ?? [];
  const recentActivity = data?.recentActivity ?? [];

  const requestsSent = summary?.requestsSent ?? 0;
  const requestsInProgress = summary?.requestsInProgress ?? 0;
  const dataDeleted = summary?.dataDeleted ?? 0;

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Typography sx={{ color: T.faint, fontSize: "0.9rem" }}>
          Failed to load dashboard. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    // FIX: Replaced the previous centering approach (which used display:flex + overflowX:hidden
    // and still allowed bounce) with a simple full-width box + constrained inner box.
    // width/maxWidth/boxSizing is the reliable way to prevent horizontal overflow on mobile.
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        py: { xs: 4, md: 6 },
        minHeight: "100vh",
        mt: 6,
        pb: 4,
        bgcolor: "#fafafa",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, md: 4 },
          boxSizing: "border-box",
        }}
      >
        {/* ── Title ── */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ fontSize: "1.1rem", fontWeight: 700, color: T.ink }}
          >
            Dashboard
          </Typography>
          <Box sx={{ borderBottom: `1px solid ${T.border}`, mt: 1 }} />
        </Box>

        {/* ── Alert Banners ── */}
        <AlertBanners />

        {/* ── Request Status Overview ── */}
        <Card sx={{ mb: 2.5, mt: 2.5 }}>
          <SectionTitle
            title="Request Status Overview"
            subtitle="Tracking all deletion requests"
          />
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="stretch">
              {[
                {
                  title: "Requests Sent",
                  value: requestsSent,
                  sub: "All brokers contacted",
                  icon: <SendIcon size={18} />,
                },
                {
                  title: "In Progress",
                  value: requestsInProgress,
                  sub: "Removals in progress",
                  icon: <RadarIcon size={18} />,
                },
                {
                  title: "Data Deleted",
                  value: dataDeleted,
                  sub: "Confirmed removals",
                  icon: <ShieldCheckIcon size={18} />,
                },
              ].map((card) => (
                <Grid key={card.title} size={{ xs: 12, sm: 4 }}>
                  {isLoading ? (
                    <Box
                      sx={{
                        bgcolor: T.surface,
                        border: `1px solid ${T.border}`,
                        borderRadius: 2,
                        p: 2,
                        height: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <Skeleton variant="rounded" width={34} height={34} />
                        <Skeleton variant="text" width={80} height={14} />
                      </Stack>
                      <Skeleton variant="text" width={50} height={44} />
                      <Skeleton
                        variant="text"
                        width="65%"
                        height={14}
                        sx={{ mt: 0.75 }}
                      />
                    </Box>
                  ) : (
                    <MetricCard
                      title={card.title}
                      value={card.value}
                      sub={card.sub}
                      icon={card.icon}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>

        {/* ── Protected Data ── */}
        <Card sx={{ mb: 2.5 }}>
          <SectionTitle
            title="Your Protected Data"
            subtitle="Information protected from data brokers"
          />
          {isLoading ? (
            <Box sx={{ p: 2.5 }}>
              {[...Array(6)].map((_, i) => (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{
                    py: 1.25,
                    borderBottom: i < 5 ? `1px solid ${T.border}` : "none",
                  }}
                >
                  <Skeleton variant="circular" width={14} height={14} />
                  <Skeleton variant="text" width={130} height={16} />
                  <Box flex={1} />
                  <Skeleton variant="rounded" width={100} height={5} />
                </Stack>
              ))}
            </Box>
          ) : (
            <DataExposureTable categories={categories} />
          )}
        </Card>

        {/* ── Recent Activity ── */}
        <Card sx={{ mb: 2.5 }}>
          <SectionTitle
            title="Recent Activity"
            subtitle="Latest updates across all data brokers"
            action={
              <ActionButton
                label="View All"
                onClick={() => navigate("/requests")}
              />
            }
          />
          <Box sx={{ p: 2.5 }}>
            {isLoading ? (
              <Stack spacing={1}>
                {[...Array(5)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderRadius: 2,
                      border: `1px solid ${T.border}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ flex: 1 }}
                    >
                      <Skeleton
                        variant="rounded"
                        width={36}
                        height={36}
                        sx={{ borderRadius: 2, flexShrink: 0 }}
                      />
                      <Skeleton variant="text" width={140} height={16} />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Skeleton
                        variant="rounded"
                        width={80}
                        height={20}
                        sx={{ borderRadius: 10 }}
                      />
                      <Skeleton variant="text" width={65} height={14} />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : recentActivity.length === 0 ? (
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: T.faint,
                  textAlign: "center",
                  py: 3,
                }}
              >
                No recent activity found.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {recentActivity.map((item, i) => {
                  const initials = (item.company ?? "?")
                    .split(/(?=[A-Z])|\s/)
                    .filter((w) => w.length > 0)
                    .slice(0, 2)
                    .map((w) => w[0].toUpperCase())
                    .join("");
                  return (
                    <Box
                      key={item.request_id}
                      sx={{
                        bgcolor: i % 2 === 0 ? "#FAFBFE" : "transparent",
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${T.border}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1.5,
                        transition: "background 0.2s, border-color 0.2s",
                        flexWrap: "wrap",
                        "&:hover": {
                          bgcolor: T.accentBg,
                          borderColor: "#D1D5DB",
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            bgcolor: "#FFFFFF",
                            border: `1px solid ${T.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 12, fontWeight: 700, color: T.ink }}
                          >
                            {initials}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: T.ink,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.company ?? "—"}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ flexShrink: 0 }}
                      >
                        <StatusBadge status={item.latest_status} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.75rem",
                            color: T.faint,
                            minWidth: 70,
                            textAlign: "right",
                          }}
                        >
                          {relativeDate(item.updated_at || item.created_at)}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
